// Globals
import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Form, FormItem } from '@/components';
import { Input, InputNumber, Select, TextEditor } from '@/ui';
import { StyledWrapper } from './AppraiserFormStyles';

// Helpers
import { MAX_NUMBER_LENGTH } from '@/constants/common';
import { TAppraiserFormValues } from '../../helpers/types';
import { PricesFormValues, getPropertyTypeOptions } from '../../helpers';
import { usePhotoShootingFormRules } from '../../hooks';

type TAppraiserForm = {
  onSubmit: (values: TAppraiserFormValues) => void;
  formName?: string;
  initialValues?: Partial<TAppraiserFormValues>;
};

export const AppraiserForm = ({ onSubmit, formName, initialValues }: TAppraiserForm) => {
  const rules = usePhotoShootingFormRules();
  const { t } = useTranslation(['prices', 'common']);

  return (
    <Form onFinish={onSubmit} name={formName} initialValues={initialValues}>
      <FormItem
        name={PricesFormValues.NAME_EN}
        rules={rules[PricesFormValues.NAME_EN]}
        label={t('label_price_name_en')}
      >
        <Input />
      </FormItem>
      <FormItem
        name={PricesFormValues.NAME_NO}
        rules={rules[PricesFormValues.NAME_NO]}
        label={t('label_price_name_no')}
      >
        <Input />
      </FormItem>
      <StyledWrapper>
        <FormItem
          name={PricesFormValues.FROM}
          label={t('label_price_from')}
          dependencies={[PricesFormValues.TO]}
          rules={[
            ...rules[PricesFormValues.FROM],
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value && !!getFieldValue(PricesFormValues.TO)) {
                  return Promise.reject(t('validation_price_from', { ns: 'prices' }));
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <InputNumber maxLength={MAX_NUMBER_LENGTH} />
        </FormItem>
        <FormItem
          name={PricesFormValues.TO}
          dependencies={[PricesFormValues.FROM]}
          label={t('label_price_to')}
          rules={[
            ...rules[PricesFormValues.TO],
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value == null || getFieldValue(PricesFormValues.FROM) <= value) {
                  return Promise.resolve();
                }

                return Promise.reject(t('validation_price_to', { ns: 'prices' }));
              },
            }),
          ]}
        >
          <InputNumber maxLength={MAX_NUMBER_LENGTH} />
        </FormItem>
      </StyledWrapper>
      <FormItem name={PricesFormValues.PRICE} rules={rules[PricesFormValues.PRICE]} label={t('label_price_price')}>
        <InputNumber maxLength={MAX_NUMBER_LENGTH} />
      </FormItem>
      <FormItem
        name={PricesFormValues.PROPERTY_TYPE}
        rules={rules[PricesFormValues.PROPERTY_TYPE]}
        label={t('label_property_type')}
      >
        <Select options={getPropertyTypeOptions(t)} size="large" placeholder={t('label_property_type')} />
      </FormItem>
      <FormItem
        name={PricesFormValues.DESCRIPTION_EN}
        rules={rules[PricesFormValues.DESCRIPTION_EN]}
        label={t('label_price_description_en')}
      >
        <TextEditor />
      </FormItem>
      <FormItem
        name={PricesFormValues.DESCRIPTION_NO}
        rules={rules[PricesFormValues.DESCRIPTION_NO]}
        label={t('label_price_description_no')}
      >
        <TextEditor />
      </FormItem>
    </Form>
  );
};

export default AppraiserForm;
